import { useEffect } from 'react';
import IncidentBanner from './components/IncidentBanner.js';
import LastCheckedPill from './components/LastCheckedPill.js';
import MaintenancePage from './components/MaintenancePage.js';
import TickerBanner from './components/TickerBanner.js';
import { useClock } from './hooks/useClock.js';
import { useDashboardState } from './hooks/useDashboardState.js';
import { useIncidentBgpFeed } from './hooks/useIncidentBgpFeed.js';
import LatencyProbesPage from './pages/LatencyProbesPage.js';
import { HomeContent } from './pages/Home.js';
import { usePersistedSetting } from './hooks/usePersistedSetting.js';
import { formatHeaderTimes } from './lib/formatTime.js';

type ActiveView = 'status' | 'latency';

const views = [
  { id: 'status' as const, label: 'STATUS' },
  { id: 'latency' as const, label: 'LATENCY PROBES' },
];

// App shell v0.1.1 — adds route-level tabs so visitors can switch between
// status and latency analytics with persisted per-visitor preferences.
export default function App() {
  const [active, setActiveRaw] = usePersistedSetting('secdefcon:active-view', 'status');
  const { now } = useClock();
  const { state, providers, incident, stateResource, incidentResource } = useDashboardState();
  const activeIncident = incidentResource.data ?? incident;
  const { bgp: bgpResource } = useIncidentBgpFeed(!!activeIncident?.active);
  const activeView: ActiveView = active === 'latency' ? 'latency' : 'status';

  const setActive = (next: ActiveView): void => {
    setActiveRaw(next);
  };

  useEffect(() => {
    const cls = 'incident-mode';
    if (activeIncident?.active) document.body.classList.add(cls);
    else document.body.classList.remove(cls);
    return () => document.body.classList.remove(cls);
  }, [activeIncident?.active]);

  const canRenderStatus = stateResource.ready && state && providers && state.providers;

  return (
    <div className="bg-bg text-text min-h-screen">
      <IncidentBanner incident={incidentResource} bgp={bgpResource} />
      <header className="border-b border-border px-4 py-3 flex items-center justify-between relative">
        <div className="font-mono text-sm tracking-[2px] text-accent">
          SECDEFCON · INTERNET HEALTH
        </div>
        {state ? (
          <div className="absolute left-1/2 -translate-x-1/2 hidden md:block pointer-events-none">
            <LastCheckedPill fetchedAt={state.fetched_at} now={now} />
          </div>
        ) : null}
        <div className="text-xs text-dim font-mono tabular-nums">{formatHeaderTimes(now)}</div>
      </header>
      <nav className="border-b border-border bg-bg">
        <div className="max-w-[1400px] mx-auto px-4 py-2 flex items-center gap-2">
          {views.map((view) => {
            const isActive = activeView === view.id;
            return (
              <button
                key={view.id}
                type="button"
                onClick={() => setActive(view.id)}
                className={`px-3 py-1.5 text-xs tracking-[2px] font-mono uppercase border ${
                  isActive
                    ? 'border-accent bg-accent/15 text-accent'
                    : 'border-border text-dim hover:border-accent hover:text-text'
                }`}
              >
                {view.label}
              </button>
            );
          })}
        </div>
      </nav>
      {state ? (
        <div className="md:hidden border-b border-border bg-bg py-1.5 flex justify-center">
          <LastCheckedPill fetchedAt={state.fetched_at} now={now} />
        </div>
      ) : null}
      {state ? <TickerBanner ticker={state.ticker} /> : null}
      {state?.maintenance?.enabled ? (
        <MaintenancePage maintenance={state.maintenance} />
      ) : activeView === 'latency' ? (
        <LatencyProbesPage />
      ) : canRenderStatus ? (
        <HomeContent state={state} providers={providers} />
      ) : (
        <div className="p-6 font-mono text-dim">connecting…</div>
      )}
    </div>
  );
}
